/**
 * HTTP-клиент мини-аппа.
 *
 * Токен живёт только в памяти модуля: класть его в localStorage незачем —
 * initData внутри Telegram доступен всегда, и переавторизация дешевле,
 * чем долгоживущий секрет в хранилище, переживающее закрытие приложения.
 */

import { getInitData } from '../telegram';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';

export interface AuthUser {
  id: number;
  telegram_id: number;
  role: string;
  full_name: string | null;
  has_profile: boolean;
}

interface AuthResponse {
  token: string;
  expires_in: number;
  user: AuthUser;
}

let accessToken: string | null = null;
let expiresAt = 0;
let inFlight: Promise<AuthResponse> | null = null;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function authenticate(): Promise<AuthResponse> {
  const initData = getInitData();
  if (!initData) {
    throw new ApiError(401, 'Приложение открыто не из Telegram');
  }

  const response = await fetch(`${API_BASE}/api/v1/auth`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ initData }),
  });

  if (!response.ok) {
    throw new ApiError(response.status, 'Не удалось войти');
  }

  const data = (await response.json()) as AuthResponse;
  accessToken = data.token;
  // Обновляемся на минуту раньше срока, чтобы не ловить 401 на границе.
  expiresAt = Date.now() + (data.expires_in - 60) * 1000;
  return data;
}

/** Гарантирует действующий токен. Параллельные вызовы разделяют один
 *  запрос: иначе первый же экран с тремя виджетами создаст три сессии. */
export function ensureSession(): Promise<AuthResponse> {
  if (accessToken && Date.now() < expiresAt) {
    return Promise.resolve({
      token: accessToken,
      expires_in: Math.round((expiresAt - Date.now()) / 1000),
      user: cachedUser!,
    });
  }
  if (!inFlight) {
    inFlight = authenticate()
      .then((data) => {
        cachedUser = data.user;
        return data;
      })
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
}

let cachedUser: AuthUser | null = null;

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  await ensureSession();

  const send = () =>
    fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(init.headers ?? {}),
        Authorization: `Bearer ${accessToken}`,
      },
    });

  let response = await send();

  // Токен мог протухнуть раньше расчётного срока — например, после
  // перезапуска сервера со сменой секрета. Одна молчаливая попытка.
  if (response.status === 401) {
    accessToken = null;
    expiresAt = 0;
    await ensureSession();
    response = await send();
  }

  if (!response.ok) {
    throw new ApiError(response.status, `Запрос ${path} завершился ошибкой`);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}
